import { cn } from '@reelplicate/ui';

import { STATUS_ACCENTS, STATUS_LABELS } from './calendar-model';
import { type CalendarDrag, moveToDay } from './use-drag-move';
import { addDays, formatTime, isSameDay } from './week';
import { type GridPost, PastHatch } from './week-grid';

/** Rows a cell shows before the rest fold into "+N more". */
const MAX_ROWS = 3;

type MonthGridProps = {
  /** The Monday the grid starts on (see `monthGrid`). */
  start: Date;
  weeks: number;
  /** Any date in the month shown; days outside it are muted. */
  month: Date;
  now: Date;
  posts: GridPost[];
  onOpenPost: (post: GridPost) => void;
  /** Open the Day view on this date. */
  onOpenDay: (day: Date) => void;
  /** Drag a post to another day (same time). */
  drag: CalendarDrag;
};

/**
 * Figma "Calendar · Month" (370:4587): Mon–Sun columns, one row per week.
 * Past days are hatched, today is Pale Sage, and each post is a one-line row
 * (status bar, time, title); a busy day folds the rest into "+N more".
 * Scheduled posts drag to another day, keeping their time.
 */
export function MonthGrid({
  start,
  weeks,
  month,
  now,
  posts,
  onOpenPost,
  onOpenDay,
  drag,
}: MonthGridProps) {
  const days = Array.from({ length: weeks * 7 }, (_, i) => addDays(start, i));
  const sorted = [...posts].sort((a, b) => a.start.getTime() - b.start.getTime());
  return (
    <div className="bg-bg-surface border-border-subtle w-full overflow-hidden rounded-2xl border">
      <div className="overflow-x-auto">
        <div className="grid min-w-[840px] grid-cols-7">
          {days.slice(0, 7).map((day) => (
            <div
              key={day.getDay()}
              aria-hidden
              className="border-border-subtle text-overline text-text-tertiary flex h-10 items-center justify-center border-b uppercase not-first:border-l"
            >
              {day.toLocaleString('en-US', { weekday: 'short' })}
            </div>
          ))}
          {days.map((day, i) => (
            <MonthCell
              key={day.toISOString()}
              day={day}
              now={now}
              inMonth={day.getMonth() === month.getMonth()}
              firstColumn={i % 7 === 0}
              lastRow={i >= (weeks - 1) * 7}
              posts={sorted.filter((p) => isSameDay(p.start, day))}
              onOpenPost={onOpenPost}
              onOpenDay={onOpenDay}
              drag={drag}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function MonthCell({
  day,
  now,
  inMonth,
  firstColumn,
  lastRow,
  posts,
  onOpenPost,
  onOpenDay,
  drag,
}: {
  day: Date;
  now: Date;
  inMonth: boolean;
  firstColumn: boolean;
  lastRow: boolean;
  posts: GridPost[];
  onOpenPost: (post: GridPost) => void;
  onOpenDay: (day: Date) => void;
  drag: CalendarDrag;
}) {
  const today = isSameDay(day, now);
  const past = !today && day < now;
  // The first day shown from another month carries its month ("Aug 31", "Oct 1").
  const label =
    !inMonth && (day.getDate() === 1 || firstColumn)
      ? `${day.toLocaleString('en-US', { month: 'short' })} ${day.getDate()}`
      : String(day.getDate());
  const dayLabel = day.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  const shown = posts.length > MAX_ROWS ? posts.slice(0, MAX_ROWS - 1) : posts;
  const more = posts.length - shown.length;
  const landing = drag.dragging && moveToDay(drag.dragging.start, day);
  const targeted = drag.target !== null && isSameDay(drag.target, day);

  return (
    <div
      data-testid={`month-day-${day.getMonth() + 1}-${day.getDate()}`}
      onDragOver={(event) => drag.over(event, landing)}
      onDrop={(event) => drag.drop(event, landing)}
      className={cn(
        'border-border-subtle relative flex min-h-[159px] flex-col gap-1 px-2.5 pt-2 pb-2.5',
        !firstColumn && 'border-l',
        !lastRow && 'border-b',
        today && 'bg-brand-subtle',
        targeted && 'bg-brand-subtle ring-border-brand ring-2 ring-inset',
      )}
    >
      {past && <PastHatch className="inset-0" />}
      <button
        type="button"
        aria-label={`${dayLabel}${today ? ' (today)' : ''}, open day`}
        onClick={() => onOpenDay(day)}
        className={cn(
          'text-body-lg-strong focus-visible:shadow-focus-ring relative mb-1 self-start rounded-xs px-0.5 outline-none hover:underline',
          inMonth ? 'text-text-primary' : 'text-text-tertiary',
        )}
      >
        {label}
      </button>
      <ul aria-label={dayLabel} className="relative flex flex-col gap-1">
        {shown.map((post) => (
          <li
            key={post.id}
            draggable={post.movable}
            onDragStart={(event) => drag.start(event, post)}
            onDragEnd={drag.end}
            className={cn(
              post.movable && 'cursor-grab active:cursor-grabbing',
              drag.dragging?.id === post.id && 'opacity-50',
            )}
          >
            <button
              type="button"
              aria-label={`${post.title}, ${formatTime(post.start)} · ${post.account}, ${STATUS_LABELS[post.status]}`}
              onClick={() => onOpenPost(post)}
              className="hover:bg-bg-subtle focus-visible:shadow-focus-ring flex h-5 w-full items-center gap-1.5 rounded-xs px-1 text-left outline-none"
            >
              <span
                aria-hidden
                className={cn(
                  'h-3 w-[3px] shrink-0 rounded-[2px]',
                  STATUS_ACCENTS[post.status],
                )}
              />
              <span className="text-caption text-text-tertiary shrink-0">
                {formatTime(post.start)}
              </span>
              <span className="text-caption text-text-primary truncate">
                {post.title}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {more > 0 && (
        <button
          type="button"
          onClick={() => onOpenDay(day)}
          className="text-caption text-text-secondary hover:text-text-primary focus-visible:shadow-focus-ring relative self-start rounded-xs px-1 outline-none"
        >
          +{more} more
        </button>
      )}
    </div>
  );
}
